import { button, line, type Hands, type HeldThing, type Hotbar, type MenuTab } from "./shared";
import { touchButtonLabel } from "../../input/touch";

/*
 * What the player is carrying, wearing and holding: the menu's fourth page
 * (chunk U-0's split of `panels.ts`, chunk U-3's move into the menu, chunk U-5's
 * body register, its two doors and the hotbar).
 *
 * Slots are the law (`MAX_CARRIED`, read across the boundary rather than guessed
 * at) and there is no weight column: the density chunk that would earn one does
 * not exist yet (`ui-graduation-plan.md` D7, and the sim's own hold — items are
 * weightless until a density column lands on the items table).
 *
 * **The monospace went, and the mark came out of the string.** The rows used to
 * be aligned by `white-space: pre` because the leading `▸` on the first row was
 * part of its text and a proportional space would have put the marks out of
 * line with each other. `ui/inventory.css` said in as many words what a restyle
 * owed: *"a restyle that puts a proportional face on this list has to move the
 * mark out of the string first"*. It is its own element now, so the label a row
 * shows is exactly the label the simulation composed and nothing else
 * (invariant 7).
 *
 * # The body register, and the six slots that are NOT drawn
 *
 * The lab draws one row per wearable part category — head, neck, trunk, hands,
 * legs, feet — with each worn piece in the row it protects
 * (`ui-lab/surfaces/menu-tabs.html:354`). **It cannot be drawn honestly from
 * this side of the wall, and it is not drawn.** Three facts, each checked
 * against the source rather than assumed:
 *
 * - **no part crosses.** `WasmSim::worn` returns `item, kind, material, quality`
 *   rows and says so in as many words: *"what each piece protects is
 *   deliberately absent — a client that drew 'on the head' beside a helm would
 *   be keeping a copy of the armour table and of the body plan"*
 *   (`crates/sim-wasm/src/witness.rs`, and `world/types.ts`'s `worn()` repeats
 *   it). The table it means is `combat::armour_for`, which is `pub(crate)`;
 * - **no body plan crosses either.** `sim_core::body`'s sixteen parts and ten
 *   `PartCategory` rows have no export anywhere in `sim-wasm` — not the names,
 *   not the categories, not the count. So the six row *labels* would be this
 *   client asserting what a body is made of, and bodies are data in this project
 *   by standing direction (races arrive as body plans, `lore-direction.md`);
 * - **the kind ids the map would need are half-missing.** `world/types.ts`
 *   exports seven `ITEM_*` constants checked against `item_kind_ids()` at
 *   startup, and neither `Armor` nor `Helm` is among them — so a kind→category
 *   table here would begin with two numbers nobody checks, which is the one
 *   thing this client never does with a constant.
 *
 * What is drawn instead is the register over **what does cross**: the worn
 * things in the simulation's own words and its own order, the grasp hands, and
 * who is walking with the player. The read that would earn the six slots is
 * filed in `docs/decisions/ui.md` §6 with its shape.
 *
 * # The two doors
 *
 * Chunk S-2 opened both (`docs/decisions/player.md` §24) and this is the surface
 * they were opened for.
 *
 * - **wield, on every carried row that is not already in hand.** `Command::Wield`
 *   *"asks nothing of the body or the thing — wielding a boulder is legal"*, so
 *   there is no row it would refuse and no row this page has to guess about. The
 *   button's hint carries what a wield actually means, which is the thing a
 *   player would otherwise guess wrong: ordering a pair of hands is not arming
 *   them, and what you fight with is still `combat::weapon_of`'s pick.
 * - **put on, on exactly one row: the one the simulation itself names.**
 *   `WitnessSource.wearable` is *the piece a wear would put on next* — the sim's
 *   own pick, over its own armour table and its own body plan. Pressing it sends
 *   `wear(item)`, the **named** form, rather than the bare verb: the row is what
 *   the player pressed, and between the read and the press a tick may have
 *   changed what "best" means. A button on every row was refused — the client
 *   cannot tell armour from a boulder (see above), and a *put on* on a boulder
 *   is a press the simulation refuses in silence, which is the one thing this
 *   client's verbs have never been allowed to do (`witness/arms.ts`'s first
 *   rule, the graybox ruling).
 */

/** One thing the player is carrying, as the flat call draws it. */
export interface CarriedLine {
  /**
   * What it is called, in the simulation's own words: *"masterwork oak table"*.
   *
   * Composed sim-side out of three tables the client never keeps a copy of — the
   * rung, the material and the noun (`world/types.ts`,
   * {@link WitnessSource.itemLabel}). The panel puts it on a line and writes not
   * one word of it, which is the same bargain the market board's notices and a
   * book's pages are read under.
   *
   * **The empty string is a section break** and not a thing with no name. It is
   * the caller's own punctuation: `witness/arms.ts`'s `handsLines` writes a
   * blank row before the worn list and before the quiver, and
   * `witness/company.ts`'s `companyLines` says *"a blank row before the heading,
   * like the worn section above it"*. This page draws it as a hairline rule,
   * which is what the caller meant by it, and counts against the cap only the
   * rows before the first one (see {@link InventoryPanel.show}).
   */
  readonly label: string;
}

/**
 * What the player is carrying, opened with `I` — the menu's *inventory* page.
 *
 * Three sections and a strip: the **body register** (what is worn, what is in
 * the hands, who is with you), the **carry list** with slots as the law, and the
 * **hotbar** on the foot. An item's identity is material, maker and moment (plan
 * decision 4) and every one of those is a fact the simulation composes into the
 * label — so a page with columns for them would be the client taking three
 * vocabularies apart and putting them back together in its own order.
 *
 * **What is pressable, and what is still a key out in the world.** Putting
 * something down is `Q` and stays one, because a drop names a *tile* and a page
 * has no tile — the same reason the pickup is a proximity verb rather than a row
 * in a list. `P` and `O` stay bound too, and the page's own foot still names
 * them. What the rows offer is the two doors that name a **thing in the hands**,
 * where a handle resolves against the player's own eight and nowhere else
 * (`player.md` §24.3), plus a slot on the strip — which is arrangement rather
 * than a verb at all.
 */
export class InventoryPanel {
  private readonly tab: MenuTab;
  private readonly hotbar: Hotbar | null;
  private readonly bodyColumn: HTMLElement;
  private readonly wornElement: HTMLElement;
  private readonly bodyElement: HTMLElement;
  private readonly countElement: HTMLElement;

  /**
   * The doors a row has been pressed on, waiting for `main.ts` to turn them into
   * commands.
   *
   * **Queued rather than fired**, which is `docs/decisions/input.md` §5's rule
   * read one layer up: a panel never holds a world — that is what keeps a
   * surface from being able to send anything — so a press writes the same kind
   * of latch a key writes and the frame loop drains it beside every other one.
   * A check that builds a bare `new Hud(root)` drives the rows and reads the
   * queue, which is the whole of what a client-side check of a command can be.
   */
  private queued: InventoryPress[] = [];

  /** What the page last drew, so a press can redraw it without a second read. */
  private showing: Hands | null = null;

  /**
   * `touch` decides whether the page's own note names keys or the buttons under
   * a thumb (invariant 5). What *closes* the page is the menu's and is said once
   * for all four (`hud/panels/menu.ts`).
   *
   * `hotbar` is the session's arrangement, built by `hud/hud.ts` and shared with
   * the strip in the corner of the screen — one object, two views, so the two
   * strips cannot disagree about which key holds what.
   */
  constructor(tab: MenuTab, touch = false, hotbar: Hotbar | null = null) {
    this.tab = tab;
    this.hotbar = hotbar;

    const grid = document.createElement("div");
    // Two columns: the body on the left, the hands on the right — the lab's own
    // arrangement, and the one the carry list's measure was already cut for
    // (`ui/inventory.css`'s note on the 44rem cap, written one chunk early).
    grid.className = "hud__inventory-grid";

    this.bodyColumn = document.createElement("div");
    this.bodyColumn.className = "hud__inventory-col";

    const wornTitle = document.createElement("div");
    // The lab's own heading for this column. It says what the column is rather
    // than what the page is — the tab above already says *inventory*.
    wornTitle.className = "hud__menu-heading sim-heading";
    wornTitle.textContent = "worn and held";

    this.wornElement = document.createElement("div");
    this.wornElement.className = "hud__inventory-body";

    this.bodyColumn.append(wornTitle, this.wornElement);

    const carryColumn = document.createElement("div");
    carryColumn.className = "hud__inventory-col hud__inventory-col--carry";

    const title = document.createElement("div");
    // The panel's own title, kept as a word and promoted to a section heading:
    // the tab above says *inventory*, which is what the page is, and this says
    // what the list under it is — *what you are carrying* — which is the
    // sentence the lab's page heads that column with too. It stops being
    // `hud__panel-title` because it stops being a panel's title; nothing
    // selects that name under this surface.
    title.className = "hud__menu-heading sim-heading";
    title.textContent = "what you are carrying";

    this.bodyElement = document.createElement("div");
    this.bodyElement.className = "hud__panel-body hud__panel-scroll";

    this.countElement = document.createElement("div");
    this.countElement.className = "hud__panel-count";

    carryColumn.append(title, this.bodyElement, this.countElement);
    grid.append(this.bodyColumn, carryColumn);
    tab.body.append(grid);

    if (hotbar !== null) {
      // The page's own view of the strip, on its foot where the lab drew it.
      // Always drawn, unlike the corner one: a player who opened this page is
      // asking about what is in the slots, and ten empty boxes are the answer
      // when there is nothing in them (`hud/panels/shared.ts`'s `Hotbar`).
      tab.body.append(hotbar.strip("panel"));
    }

    // What can be done *to* what is on this page, and nothing about closing it.
    // Composed from the button table on a touchscreen rather than typed, which
    // is `docs/decisions/input.md` §9's rule: the label is a string
    // `input/touch.ts` owns, and a renamed button would otherwise leave this
    // page naming something that is not on the pad.
    //
    // **The keys are unchanged and the note still names them.** The row buttons
    // are a second way to the same two doors and a first way to a third, and a
    // page that stopped naming `Q`, `P` and `O` would be a page that took three
    // bindings off a player's hands to save itself a clause.
    tab.note(
      touch
        ? `${touchButtonLabel("drop")} puts down what is at the top` +
            ` · ${touchButtonLabel("wear")} · ${touchButtonLabel("doff")}`
        : "Q puts down what is at the top · P puts on · O takes off · 1–0 hold a slot",
    );
  }

  get open(): boolean {
    return this.tab.showing;
  }

  /**
   * Every door pressed on a row since the last call, consuming them.
   *
   * Drained by `main.ts` beside the hotbar's own queue and the key latches. Both
   * verbs name a **thing in the hands**, where a display number resolves against
   * the player's own eight handles and nowhere else — which is why naming one is
   * allowed at all, and why a handle the player no longer holds resolves to
   * nothing rather than to somebody else's boulder (`player.md` §24.3).
   */
  takeDoors(): readonly InventoryPress[] {
    const queued = this.queued;
    this.queued = [];

    return queued;
  }

  /**
   * Fills the page from what the simulation says is in the hands and on the
   * body.
   *
   * `capacity` is the simulation's own cap and is only ever *shown* (the
   * client's copy is {@link MAX_CARRIED}): a ninth pickup is refused sim-side
   * whatever this page says, and knowing the number is what lets the player be
   * told rather than left wondering why a key stopped working.
   *
   * The order is the simulation's — **carry order, with the wielded thing at
   * the head of it** (`player.md` §24.2) — which is the order a drop walks, so
   * the top row is still the one `Q` puts down and the mark on it still says so.
   */
  hold(hands: Hands): void {
    this.showing = hands;
    // The strip first, because it forgets a slot whose thing is no longer
    // carried and the rows below read back which slot each thing is in.
    this.hotbar?.read(hands);

    const rows = register(hands);

    this.wornElement.replaceChildren(...rows);
    // **The column goes when there is nothing it may say**, which is exactly the
    // flat call over an empty pair of hands: that call carries no sections, so
    // the register knows neither what is worn nor what is in hand, and a heading
    // with nothing under it reads as a surface that failed rather than as a
    // player with nothing. The structured call always has at least its two
    // honest empty states, so this never fires under it.
    this.bodyColumn.hidden = rows.length === 0;

    this.bodyElement.replaceChildren(
      ...(hands.carried.length === 0
        ? [line("your hands are empty")]
        : hands.carried.map((thing, index) => this.carryRow(thing, index, hands))),
    );

    this.countElement.textContent =
      hands.carried.length === 0 ? "" : `${hands.carried.length} of ${hands.capacity}`;
    this.tab.reveal();
  }

  /**
   * The flat call, kept working byte for byte because its one caller is fenced
   * off.
   *
   * `witness/witness.ts` hands over one flat list with the worn things, the
   * quiver and the company appended to the carried ones and blank rows between
   * them (`witness/arms.ts`'s `handsLines`, `witness/company.ts`'s
   * `companyLines`). That file owns seventy-five `hud.*` call sites and is
   * fenced out of every chunk in this wave (plan §3 invariant 10), so the one
   * expression that would retire this method is written in `docs/decisions/ui.md`
   * §6 for whoever opens it.
   *
   * **It is re-expressed over {@link InventoryPanel.hold} rather than drawing a
   * second way**, so there is one draw path: the rows before the caller's first
   * break are the hands, they carry no handles because none crossed, and a row
   * with no handle is offered no door. Everything after the break goes into
   * {@link Hands.also} — the composers' own words in the composers' own order,
   * breaks and all — and the register draws that instead of its three sections,
   * because a flat list says nothing about which of them a row belongs to and an
   * empty state written over silence would be an invention. When the structured
   * call lands, this method, {@link Hands.also} and {@link CarriedLine} go
   * together.
   */
  show(lines: readonly CarriedLine[], capacity: number): void {
    const broken = lines.findIndex((entry) => entry.label === "");
    const hands = broken < 0 ? lines : lines.slice(0, broken);

    this.hold({
      carried: hands.map((entry) => ({ item: null, label: entry.label })),
      worn: [],
      wielded: null,
      wearable: null,
      quiver: "",
      company: [],
      also: broken < 0 ? [] : lines.slice(broken + 1).map((entry) => entry.label),
      capacity,
    });
  }

  hide(): void {
    this.tab.close();
  }

  /**
   * One row of the carry list: the words, and whatever can be done to the thing.
   *
   * The mark on the first row is the drop verb's target and it is **its own
   * element**: it used to be two characters on the front of the label, aligned
   * by a monospace, and a proportional face is what took that arrangement away
   * (`ui/inventory.css`'s own warning, discharged). What the row says is exactly
   * `HeldThing.label` and nothing the client added to it.
   */
  private carryRow(thing: HeldThing, index: number, hands: Hands): HTMLElement {
    const element = document.createElement("div");
    element.className = "hud__inventory-row";

    if (index === 0) {
      const mark = document.createElement("span");
      // The first row is the one the drop verb takes, marked so the note at the
      // foot is a fact a reader can see rather than a rule they have to
      // remember. It is the wielded thing whenever there is one, because a wield
      // files the thing at the head of the carry order and `drop_one` walks that
      // order — one fact, drawn once.
      mark.className = "hud__inventory-mark";
      mark.setAttribute("aria-hidden", "true");
      mark.textContent = "▸";
      element.append(mark);
    }

    const label = document.createElement("span");
    label.className = "hud__inventory-label";
    label.textContent = thing.label;
    element.append(label);

    const acts = document.createElement("span");
    acts.className = "hud__inventory-acts";
    element.append(acts);

    if (thing.item === null) {
      // A row this client has only the words for. No handle, no door — see
      // {@link InventoryPanel.show}.
      return element;
    }

    const item = thing.item;

    if (item === hands.wielded) {
      // Not a button, because there is nothing to press: this is already the
      // thing in front. The simulation's word for the state, in the sans an
      // interface speaks in (chunk U-1's register).
      const state = document.createElement("span");
      state.className = "hud__inventory-state";
      state.textContent = "in hand";
      acts.append(state);
    } else {
      acts.append(
        act(
          "wield",
          "holds it in front. Ordering a pair of hands is not arming them — what you fight" +
            " with is still the simulation's own pick.",
          () => {
            this.queued.push({ verb: "wield", item });
          },
        ),
      );
    }

    if (item === hands.wearable) {
      // **One row, and the simulation chose it.** See the header: this client
      // cannot tell armour from a boulder, so the only row it may offer the wear
      // door on is the one `WitnessSource.wearable` names.
      acts.append(
        act("put on", "the piece the simulation would put on next.", () => {
          this.queued.push({ verb: "wear", item });
        }),
      );
    }

    if (this.hotbar !== null) {
      acts.append(this.slotButton(item));
    }

    return element;
  }

  /**
   * The row's own slot control: one button, two states, both reversible.
   *
   * In a slot, it names the digit and pressing it takes the thing out again; out
   * of one, it says so and pressing it puts the thing in the lowest free slot. A
   * `1…0` chooser on every row was refused for the arithmetic: eight rows times
   * ten digits is eighty buttons on a page whose whole content is eight short
   * phrases.
   *
   * **Disabled rather than refused when the strip is full**, which is the only
   * way this press can fail: the ten slots are drawn directly underneath, so a
   * player can see why without being told.
   */
  private slotButton(item: number): HTMLElement {
    const hotbar = this.hotbar;

    if (hotbar === null) {
      return document.createElement("span");
    }

    const slot = hotbar.slotOf(item);

    if (slot !== null) {
      return act(`slot ${(slot + 1) % 10}`, "takes it out of the hotbar.", () => {
        hotbar.clear(item);
        this.redraw();
      });
    }

    return act(
      "to slot",
      "puts it in the first free hotbar slot. The arrangement is this session's own.",
      () => {
        hotbar.place(item);
        this.redraw();
      },
      hotbar.full,
    );
  }

  /**
   * Draws the page again from what it last drew.
   *
   * Only the *arrangement* changes under a slot press, and the arrangement is
   * this client's — so nothing is asked of the simulation and the rows are
   * rebuilt from the read that is already in hand. A press that changes the
   * world (a wield, a wear) deliberately does **not** call this: what it changed
   * is the simulation's, and the page finds out about it the way it finds out
   * about everything else, on the next read.
   */
  private redraw(): void {
    if (this.showing !== null) {
      this.hold(this.showing);
    }
  }
}

/**
 * One door pressed on one row.
 *
 * Two verbs and one handle, which is the whole of what this page may ask of the
 * world: {@link WorldSource.wield} holds a carried thing in front, and
 * {@link WorldSource.wear} puts a carried piece on **by name** — the form chunk
 * S-2 grew for exactly this surface, rather than the bare verb that means
 * *whatever the world would have picked*.
 */
export interface InventoryPress {
  readonly verb: "wield" | "wear";
  readonly item: number;
}

/**
 * The body register: what is worn, what is in the hands, and who is with you.
 *
 * **Not the lab's six part slots**, and the header says why in full: no part, no
 * body plan and no armour table crosses this wall, so a row saying *head* with a
 * helm in it would be the client keeping a copy of two simulation tables and
 * asserting a body plan besides. What is drawn is the simulation's own list in
 * the simulation's own order, which is the whole of what crossed.
 *
 * Every empty section says it in words rather than leaving a gap: an absence is
 * a state this page has, and most sessions are in all three of them.
 */
function register(hands: Hands): HTMLElement[] {
  const rows: HTMLElement[] = [];

  if (hands.also !== undefined) {
    // The flat call's remainder, and the whole of what this column may say under
    // it: a flat list carries no sections, so which of the three a row belongs
    // to is not known here and an empty state written over that silence would be
    // this page inventing an answer. The breaks are drawn as the hairlines the
    // composers meant by them, which is what this page has done with them since
    // chunk U-3.
    for (const label of hands.also) {
      rows.push(label === "" ? plainBreak() : wornRow("", label, ""));
    }

    return rows;
  }

  if (hands.worn.length === 0) {
    rows.push(bare("worn", "nothing on your body"));
  } else {
    for (const [index, thing] of hands.worn.entries()) {
      // The first worn piece is the one `O` takes off, which is `hands::doff`'s
      // own tie-break read the way the carry list's mark reads `drop_one`'s.
      rows.push(wornRow(index === 0 ? "worn" : "", thing.label, "worn"));
    }
  }

  rows.push(
    hands.wielded === null
      ? bare("in hand", "nothing in hand")
      : wornRow("in hand", labelOf(hands, hands.wielded), "wielded"),
  );

  if (hands.quiver !== "") {
    // The arrow count, which is a fact about the hands and belongs beside them
    // rather than at the end of a list of things (`witness/arms.ts` composes the
    // words; this puts them in a row).
    rows.push(wornRow("quiver", hands.quiver, ""));
  }

  for (const [index, name] of hands.company.entries()) {
    // **"with you" and not "held"**, which the lab's page draws. A companion is
    // somebody walking beside the player (`Command::Beckon`,
    // `witness/company.ts`), not a body in their arms — the sim has that second
    // relation (`PresentFigure.heldBy`) and this page is not given it. Filed
    // rather than fudged: `docs/decisions/ui.md` §6.
    rows.push(wornRow(index === 0 ? "with you" : "", name, ""));
  }

  return rows;
}

/** One row of the register: which grasp or place, what is there, and its state. */
function wornRow(place: string, label: string, state: string): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__worn-row";

  const where = document.createElement("span");
  where.className = "hud__worn-part";
  where.textContent = place;

  const what = document.createElement("span");
  // The world's own words for the thing, so serif (chunk U-1's register).
  what.className = "hud__worn-item";
  what.textContent = label;

  const said = document.createElement("span");
  // The simulation's word for the state, and *wielded* is the only one that is
  // gold: it is the one the player chose.
  said.className = state === "wielded" ? "hud__worn-state hud__worn-state--held" : "hud__worn-state";
  said.textContent = state;

  element.append(where, what, said);

  return element;
}

/**
 * A section break, which is what an empty label means when it arrives from the
 * flat call.
 *
 * `witness/arms.ts` writes a blank row before the worn list and before the
 * quiver, and `witness/company.ts` says *"a blank row before the heading, like
 * the worn section above it"* — so the caller's punctuation is a break, and a
 * break drawn as a break is a hairline with air round it rather than an empty
 * row that reads as a thing with no name.
 */
function plainBreak(): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__inventory-break";
  element.setAttribute("aria-hidden", "true");

  return element;
}

/** The same row with nothing in it, said in words rather than left blank. */
function bare(place: string, said: string): HTMLElement {
  const element = wornRow(place, said, "");
  const what = element.querySelector(".hud__worn-item");

  what?.classList.add("hud__worn-item--bare");

  return element;
}

/** The words for one handle, out of the read that is already in hand. */
function labelOf(hands: Hands, item: number): string {
  return (
    hands.carried.find((thing) => thing.item === item)?.label ??
    // A wielded thing is filed at the head of the carry list, so this is
    // unreachable while the read is one read (`player.md` §24.2). It is answered
    // rather than asserted away for `input/touch.ts`'s reason: an honest blank
    // is a row with nothing in it, and a thrown error is a page that vanished.
    ""
  );
}

/**
 * A door on a row, in the kit's quiet register.
 *
 * It borrows {@link button}'s two input rules — pointer events back on, and the
 * click stopped before it reaches the canvas that would take the pointer lock
 * back — and then takes the kit's `.panel-button` instead of the client's own
 * `hud__panel-button`. That is deliberate and it is U-2's open question read as
 * an instruction (`ui.md` §3.10 item 2): `ui/kit.css` is imported first, so a
 * `hud__` class that states a colour beats the kit whatever the markup says, and
 * a name nobody has styled is the only way to let the kit show through. Three
 * gold-edged buttons on each of eight rows is a page of emphasis, which is a
 * page of none.
 */
function act(label: string, hint: string, onPress: () => void, disabled = false): HTMLButtonElement {
  const element = button(label, onPress, disabled);

  element.className = "hud__inventory-act panel-button";
  element.title = hint;

  return element;
}
